import DsButton from './Button'

/** Kept in one place: the bar's own height, and the spacer that reserves it. */
const BAR_HEIGHT = 'h-[62px]';

/**
 * One way to save, in every section that can be edited.
 *
 * Each form used to end in a Save button of its own, which meant the control
 * sat wherever that form happened to stop — sometimes below the fold, always in
 * a different place per tab.
 *
 * It is fixed to the bottom of the screen rather than sticky inside the
 * scroller: sticky only pins while there is content below it, so on a short tab
 * the bar came to rest halfway up the page, which read as a stray row rather
 * than as the footer of the screen. Fixed, it is always the last thing above
 * the mobile tab bar, and the spacer below keeps the final field clear of it.
 *
 * The offsets follow the shell: the sidebar is 232px wide from `lg` up, and the
 * bottom tab bar is 75px plus the safe area below that breakpoint.
 */
const SettingsSaveBar = ({
  dirtyCount,
  isSubmitting = false,
  canSave = true,
  onSave,
  onDiscard,
}: {
  /** Nothing is drawn at zero: a bar that is always there is furniture. */
  dirtyCount: number,
  isSubmitting?: boolean,
  /** False while the form holds a value the API would reject. */
  canSave?: boolean,
  onSave: () => void,
  onDiscard: () => void,
}) => {
  if (dirtyCount <= 0) {
    return null;
  }

  return (<>
    <div aria-hidden="true" className={`${BAR_HEIGHT} flex-none`} />
    <div
      className={[
        'fixed z-30 left-0 right-0 lg:left-[232px]',
        'bottom-[calc(75px_+_env(safe-area-inset-bottom))] lg:bottom-0',
        BAR_HEIGHT,
        'px-4 lg:px-8 bg-navy-800 border-t border-navy-500',
        'flex items-center justify-between gap-3',
      ].join(' ')}
    >
      <span className="min-w-0 truncate text-ds-135 text-navy-200">
        {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'} in this section
      </span>
      <div className="flex items-center gap-2 flex-none">
        <DsButton variant="outline" onClick={onDiscard} isDisabled={isSubmitting}>
          Discard
        </DsButton>
        <DsButton variant="primary" onClick={onSave} isDisabled={isSubmitting || !canSave}>
          {isSubmitting ? 'Saving…' : 'Save changes'}
        </DsButton>
      </div>
    </div>
  </>);
}

export default SettingsSaveBar
